/**
 * Yandex.Metrika loader — consent-gated, production-domain-gated.
 *
 * The actual invocation decision (load vs. don't) lives in the analytics
 * provider, which watches the consent store. This module only exposes the
 * mechanics of injecting tag.js and firing the counter init once.
 *
 * Two guards fire before the network request, in order:
 *   1. Hostname whitelist — only the production host loads the counter.
 *      Dev, vite preview, staging, and preview deploys are silenced here
 *      so the Metrika dashboard never sees non-production traffic even
 *      if someone accepts cookies locally.
 *   2. In-memory load flag — idempotent; calling this after a successful
 *      load is a no-op. Prevents double-init during rapid consent toggles.
 */

const YM_COUNTER_ID = 108572436

const PRODUCTION_HOSTS = new Set([
  'marketassist.ru',
  'www.marketassist.ru',
])

// Global on window for ad-hoc tracking calls from app code. Shape matches
// the stub produced by the official Yandex snippet.
declare global {
  interface Window {
    ym?: ((...args: unknown[]) => void) & { a?: unknown[], l?: number }
  }
}

let loaded = false

/**
 * Returns true when this page is on a production host. Exposed so callers
 * (e.g. analytics provider, tests) can skip consent evaluation entirely in
 * non-production environments.
 */
export function isYandexMetricaLoadable(): boolean {
  if (typeof window === 'undefined')
    return false
  return PRODUCTION_HOSTS.has(window.location.hostname)
}

/**
 * Inject the Yandex.Metrika tag.js and fire the counter's init. Idempotent.
 * Safe to call on non-production hosts — returns without touching the DOM
 * or the network.
 */
export function loadYandexMetrica(): void {
  if (loaded)
    return
  if (!isYandexMetricaLoadable())
    return

  // Set early so a second watcher firing before the script lands does not
  // queue up another injection.
  loaded = true

  // Yandex.Metrika bootstrap — semantically equivalent to the vendor's
  // recommended snippet. We install a queueing stub on window.ym so any
  // ym(...) calls made before tag.js lands are captured in `ym.a` and
  // replayed once the real library takes over.
  type YmStub = ((...args: unknown[]) => void) & { a?: unknown[], l?: number }
  const scriptSrc = `https://mc.yandex.ru/metrika/tag.js?id=${YM_COUNTER_ID}`

  if (!window.ym) {
    const stub = function (...args: unknown[]): void {
      (stub.a ??= []).push(args)
    } as YmStub
    stub.l = Number(new Date())
    window.ym = stub
  }

  // Guard against double injection if something else already added tag.js.
  const alreadyInjected = Array.from(document.scripts).some(
    s => s.src === scriptSrc,
  )
  if (!alreadyInjected) {
    const script = document.createElement('script')
    const firstScript = document.getElementsByTagName('script')[0]
    script.async = true
    script.src = scriptSrc
    firstScript?.parentNode?.insertBefore(script, firstScript)
  }

  window.ym?.(YM_COUNTER_ID, 'init', {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    referrer: document.referrer,
    url: location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  })
}

/**
 * Intent-only revoke. Yandex.Metrika's tag.js cannot be unloaded from a
 * running document — this marker stops future `loadYandexMetrica()` calls
 * from re-initializing the counter in the same session. True revocation
 * takes effect at the next page load, where the consent store will
 * already report `analytics: false` and the loader will never run.
 */
export function resetYandexMetrica(): void {
  // Keep the loaded flag set so a flip back to granted does not cause
  // a second init on a counter that may already be running.
  loaded = true
}
